import { CircularProgress } from '@mui/material';
import { RiAddLine } from '@remixicon/react';
import { useFormik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';
import { SideDrawer, SideDrawerHeader, useSideDrawer } from 'src/components/containers/SideDrawer';
import { AgButton, AgCheckbox, AgInput, AgText } from 'src/components/ui';
import { AgGooglePlacesAutocomplete } from 'src/components/ui/AgGooglePlacesAutocomplete';
import { z } from 'zod';
import { ADDRESS_ROLE_MAP } from '../businessEntity/constants';
import { OrgPlace, useCreateOrgPlace, useUpdateOrgPlace } from './myAddresses.api';

const DRAWER_ID = 'my-address';

type AddressRole = 'BILLING' | 'SHIPPING' | 'WAREHOUSE' | 'PRIMARY_BUSINESS' | 'SUBSIDIARY' | 'OTHER';

type AddressForm = {
  description: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  postalCode: string;
  addressCountry: string;
  phone: string;
  addressRoles: AddressRole[];
  googlePlaceId: string;
};

const addressSchema = z.object({
  description: z.string().optional(),
  streetAddress1: z.string().min(1, 'Street address is required'),
  city: z.string().min(1, 'City is required'),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  addressCountry: z.string().optional(),
  phone: z.string().optional(),
});

type MyAddressDrawerProps = { mode?: 'create' | 'edit'; addressData?: OrgPlace | null; onClose?: () => void };

export function MyAddressDrawer({ mode = 'create', addressData, onClose }: MyAddressDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openDrawer, closeDrawer } = useSideDrawer();
  const { createPlace } = useCreateOrgPlace();
  const { updatePlace } = useUpdateOrgPlace();
  const flags = useFlags();

  // Build address roles list based on feature flags (matching original AddressBookCell behavior)
  const availableAddressRoles = useMemo(() => {
    const roles: AddressRole[] = ['WAREHOUSE', 'BILLING', 'PRIMARY_BUSINESS'];
    if (flags.multipleSubsidiaries) {
      roles.push('SUBSIDIARY');
    }
    roles.push('OTHER');
    return roles;
  }, [flags.multipleSubsidiaries]);

  useEffect(() => {
    if (addressData && mode === 'edit') {
      openDrawer(DRAWER_ID);
    }
  }, [addressData, mode, openDrawer]);

  const getInitialValues = (): AddressForm => {
    if (mode === 'edit' && addressData) {
      return {
        description: addressData.description || '',
        streetAddress1: addressData.streetAddress1 || '',
        streetAddress2: addressData.streetAddress2 || '',
        city: addressData.city || '',
        state: addressData.state || '',
        postalCode: addressData.postalCode || '',
        addressCountry: addressData.addressCountry || '',
        phone: addressData.phone || '',
        addressRoles: (addressData.addressRoles || []) as AddressRole[],
        googlePlaceId: addressData.googlePlaceId || '',
      };
    }
    return {
      description: '',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      phone: '',
      addressRoles: [],
      googlePlaceId: '',
    };
  };

  const f = useFormik<AddressForm>({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validate: (values) => {
      try {
        addressSchema.parse(values);
        return {};
      } catch (err) {
        if (err instanceof z.ZodError) {
          const errors: Partial<Record<keyof AddressForm, string>> = {};
          for (const issue of err.issues) {
            const field = issue.path[0] as keyof AddressForm;
            errors[field] = issue.message;
          }
          return errors;
        }
        return {};
      }
    },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const input = {
          description: values.description || null,
          streetAddress1: values.streetAddress1,
          streetAddress2: values.streetAddress2 || null,
          city: values.city,
          state: values.state || null,
          postalCode: values.postalCode || null,
          addressCountry: values.addressCountry || null,
          phone: values.phone || null,
          addressRoles: values.addressRoles,
          googlePlaceId: values.googlePlaceId || null,
        };

        if (mode === 'edit' && addressData?.id) {
          await updatePlace(addressData.id, input);
        } else {
          await createPlace(input);
        }
        resetForm();
        closeDrawer();
        onClose?.();
      } catch (error) {
        console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} address:`, error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleAddressSelect = (
    address: { streetAddress1: string; city: string; state: string; postalCode: string; addressCountry: string } | null
  ) => {
    if (!address) return;

    f.setValues({
      ...f.values,
      streetAddress1: address.streetAddress1,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      addressCountry: address.addressCountry,
    });
  };

  const drawerTitle = mode === 'edit' ? 'Edit Address' : 'Add Address';
  const submitButtonText = mode === 'edit' ? 'Update' : 'Add';

  return (
    <>
      {mode === 'create' && (
        <AgButton variant="primary" onPress={() => openDrawer(DRAWER_ID)}>
          <RiAddLine size={20} />
          Add Address
        </AgButton>
      )}
      <SideDrawer drawerId={DRAWER_ID} onClose={onClose}>
        <SideDrawerHeader title={drawerTitle} />
        <form onSubmit={f.handleSubmit}>
          <div className="flex min-w-[500px] flex-col gap-6">
            <AgInput
              label="Name"
              name="description"
              value={f.values.description}
              onChange={(value) => f.setFieldValue('description', value)}
              onBlur={() => f.setFieldTouched('description', true)}
              isInvalid={f.touched.description && !!f.errors.description}
              errorMessage={f.touched.description ? f.errors.description : undefined}
            />

            <AgGooglePlacesAutocomplete
              label="Street Address"
              value={f.values.streetAddress1}
              onChange={handleAddressSelect}
              onInputChange={(value) => f.setFieldValue('streetAddress1', value)}
              isRequired
              isInvalid={f.touched.streetAddress1 && !!f.errors.streetAddress1}
              errorMessage={f.touched.streetAddress1 ? f.errors.streetAddress1 : undefined}
            />

            <AgInput
              label="Street Address 2"
              name="streetAddress2"
              value={f.values.streetAddress2}
              onChange={(value) => f.setFieldValue('streetAddress2', value)}
              onBlur={() => f.setFieldTouched('streetAddress2', true)}
            />

            <div className="grid grid-cols-2 gap-4">
              <AgInput
                isRequired
                label="City"
                name="city"
                value={f.values.city}
                onChange={(value) => f.setFieldValue('city', value)}
                onBlur={() => f.setFieldTouched('city', true)}
                isInvalid={f.touched.city && !!f.errors.city}
                errorMessage={f.touched.city ? f.errors.city : undefined}
              />

              <AgInput
                label="State"
                name="state"
                value={f.values.state}
                onChange={(value) => f.setFieldValue('state', value)}
                onBlur={() => f.setFieldTouched('state', true)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <AgInput
                label="Postal Code"
                name="postalCode"
                value={f.values.postalCode}
                onChange={(value) => f.setFieldValue('postalCode', value)}
                onBlur={() => f.setFieldTouched('postalCode', true)}
              />

              <AgInput
                label="Country"
                name="addressCountry"
                value={f.values.addressCountry}
                onChange={(value) => f.setFieldValue('addressCountry', value)}
                onBlur={() => f.setFieldTouched('addressCountry', true)}
              />
            </div>

            <AgInput
              label="Phone"
              name="phone"
              value={f.values.phone}
              onChange={(value) => f.setFieldValue('phone', value)}
              onBlur={() => f.setFieldTouched('phone', true)}
            />

            <div className="flex flex-col gap-2">
              <AgText size="14" weight="medium" color="primary">
                Address Roles
              </AgText>
              <div className="flex flex-wrap gap-4">
                {availableAddressRoles.map((role) => (
                  <AgCheckbox
                    key={role}
                    label={ADDRESS_ROLE_MAP[role]}
                    isSelected={f.values.addressRoles.includes(role)}
                    onChange={(checked) => {
                      if (checked) {
                        f.setFieldValue('addressRoles', [...f.values.addressRoles, role]);
                      } else {
                        f.setFieldValue(
                          'addressRoles',
                          f.values.addressRoles.filter((r) => r !== role)
                        );
                      }
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <div className="grow" />
              <AgButton
                variant="white"
                onPress={() => {
                  f.resetForm();
                  closeDrawer();
                  onClose?.();
                }}
                isDisabled={isSubmitting}
              >
                Cancel
              </AgButton>
              <AgButton type="submit" variant="primary" isDisabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={20} /> : null}
                {isSubmitting ? `${mode === 'edit' ? 'Updating' : 'Adding'}...` : submitButtonText}
              </AgButton>
            </div>
          </div>
        </form>
      </SideDrawer>
    </>
  );
}

export { DRAWER_ID as MY_ADDRESS_DRAWER_ID };
